// src/help/pageHelp.js
//
// Single source of truth for the in-app help copy — the "what does this page do
// and how do I use it" text shown by components/HelpDrawer.jsx (the nav's "?"
// button, scoped to the current route) and pages/HelpPage.jsx (the /help index).
//
// Long-form companion: docs/tools-guide.md. That doc carries the same material
// with more prose; this file is the condensed version the UI renders. When a
// page's workflow changes, update BOTH — the doc is for the repo, this is for
// the person actually looking at the screen.
//
// Shape per entry:
//   { path, label, group, needsTraffic, what, useWhen, steps[], gotchas[] }
// `needsTraffic` drives the "needs a live capture" / "works standalone" chip —
// the single most common reason a page looks broken is that nothing is in scope.

export const HELP_GROUPS = {
  main: 'Dashboard',
  tools: 'Tools',
  admin: 'Admin',
};

export const PAGE_HELP = [
  // ── Dashboard ────────────────────────────────────────────────────────────
  {
    path: '/',
    label: 'Dashboard',
    group: 'main',
    needsTraffic: true,
    what: 'The live request table, and the source of truth every other page reads from.',
    useWhen: 'You want to watch traffic in real time, filter to what matters, and read the parsed validation on a single request.',
    steps: [
      'Point the device at the proxy (Admin Panel → QR or the iOS profile) and exercise the app. Rows arrive live, newest first, classified by request type.',
      'Filter by request type, host, device, or step marker. "Hide trackers" cuts beacon noise — lifecycle assembly still sees them, so nothing is lost.',
      'Click a row: request/response, decoded params, validData, and the issues the proxy flagged (each with a code and severity).',
      'Act on it — Replay (edit and re-fire, diff against the original, copy as cURL), Copy ticket (markdown summary for a bug), or Run checks (sends your selection to Checks as its scope).',
      'Summary tab: per-slot lifecycle — GAM → Prebid/APS → mediation → impression → click — with proven/inferred/guessed confidence per link, plus orphan and duplicate detection.',
      'Watch bell: proactive trend-anomaly alerts. Click one to jump to the request that triggered it.',
    ],
    gotchas: [
      'The newest 300 rows keep full bodies; older rows keep metadata only. An empty body on an old row is retention, not a capture failure.',
      'A cert-pinned host shows as a TLS drop, not as missing traffic. Add it to the SSL-exclude list in Config if it breaks the app.',
      '"New session" clears for real — local state and the proxy buffer — after a confirm.',
    ],
  },

  // ── Tools ────────────────────────────────────────────────────────────────
  {
    path: '/traffic',
    label: 'Traffic control → IU Rewrite',
    group: 'tools',
    needsTraffic: false,
    what: 'Live ad-unit and size override: match an ad unit path, replace it with another.',
    useWhen: 'You want production app traffic to hit test ad units, or need to force a size or a single-ad-source setup without a new app build.',
    steps: [
      'Add a rule: match (wildcard/prefix, e.g. /4145/fnc/iosmob/hb/*) → replace (e.g. /4145/FNC.test/app/banner).',
      'Toggle the individual rule on.',
      'Toggle the master switch on — rewriting only happens when both are on.',
      'Re-trigger the ad in the app and confirm on the Dashboard that the outgoing iu changed.',
    ],
    gotchas: [
      'Rules apply live, with no proxy restart, to every device pointed at this proxy. Turn the master switch off when you are done — this is the usual answer to "why is my traffic going to a test unit?"',
      'Single-ad-source testing cannot be forced from here — GAM ignores a buyer restriction added in transit. Use Ad Inspector → Ad source testing on a registered test device.',
    ],
  },
  {
    path: '/privacy',
    label: 'Privacy → Data leaving the device',
    group: 'tools',
    needsTraffic: true,
    what: 'A live view of what data is leaving the device, and to whom.',
    useWhen: 'Privacy review — which vendors receive which data types, and which requests trip a disclosure rule.',
    steps: [
      'Capture a session normally.',
      'Read the summary tiles: scanned / flagged / vendors / high-risk.',
      'Check the findings strip (F1…F6) — only firing rules are shown.',
      'Scan the newest-first feed of flagged requests, coloured by severity.',
      'Expand the vendor × data-type matrix for the full picture to hand to legal or privacy.',
    ],
    gotchas: [],
  },
  {
    path: '/checks',
    label: 'Checks',
    group: 'tools',
    needsTraffic: true,
    what: 'The conformance engine — validation written in plain English, compiled into structured checks and run against captured traffic. Also the one place to ask questions about the traffic.',
    useWhen: 'You want to validate something specific, or ask "did X actually happen in this session?"',
    steps: [
      'Pick a mode: Agent (run and ask), Sign off (verdict per acceptance criterion — formerly Launch), Rules (add or switch off rules — formerly its own page). Driving a build on a device (formerly Build) is the Drive option inside a launch\'s Verify card.',
      'Tool Driven — describe what you want in English and let the tool generate the traffic itself (device simulator or web) before validating. Turn on Driven when a device is connected; otherwise it runs passively over what is already captured.',
      'Manual — works over requests you already have; this is where a Dashboard "Run checks" handoff lands. Author one check and Compile it (the proxy turns your sentence into a structured check plus a rationale), or use "Generate checks with AI" for a whole proposed set to review.',
      'Suites — run a saved suite. adtech-baseline is the deterministic baseline and runs itself whenever there is ad-relevant traffic.',
      'Read the Rollup for pass/fail counts, then the results table (status, value read, got vs expected).',
      'Open Evidence to see the actual matching rows and click through to the request on the Dashboard. Expand key/code coverage to see which keys or issue codes the check saw.',
      'Use Ask for a follow-up in plain English — it investigates the traffic (bodies, validators, correlations) rather than guessing.',
    ],
    gotchas: [
      'Checks are ad-scoped by default: confidently non-ad traffic is excluded, unknown stays in. The scope banner says what was in scope — override it when a check legitimately needs wider scope (e.g. "prove tracker X never fires").',
      'Naming a vendor in your sentence filters to that vendor. That is intentional.',
      'Flaky checks are auto-quarantined so they stop failing the suite. Review and release them from the Quarantined checks panel.',
    ],
  },
  {
    path: '/checks?mode=signoff',
    label: 'Checks → Sign off',
    group: 'tools',
    needsTraffic: true,
    what: 'Feature sign-off driven by acceptance criteria — paste the ACs from the ticket, get a criterion-by-criterion verdict with evidence.',
    useWhen: 'A feature is going out and you need "did we meet each AC?" rather than a wall of requests.',
    steps: [
      'New launch — Title, Ticket ref (JIRA key), App, Build.',
      'Paste the acceptance criteria, one per line, and save.',
      'Compile criteria — each AC is mapped to an existing check, an AI-authored check, or a manual procedure (some ACs genuinely cannot be checked on the wire; those get a manual verification note instead).',
      'Produce traffic in scope: tap through the app yourself (Live), or let the tool install and drive the build (Drive — needs a controllable simulator, device or browser).',
      'Run checks — results are grouped by criterion with a worst-of rollup: Met / Not met / Needs review / Manual review / Not run.',
      'On any Not-met criterion, Copy ticket gives you a complete bug report: expected vs actual, spec citation, deep link to the request, replay cURL.',
    ],
    gotchas: [
      'Scope mirrors Checks: a Dashboard row selection wins; otherwise the whole session is used.',
      'Once the launch is saved, its own App field (not the Dashboard app dropdown) drives compile and run.',
    ],
  },
  {
    path: '/inspect?tab=diff',
    label: 'Inspect → Compare',
    group: 'tools',
    needsTraffic: false,
    what: 'Field-by-field comparison of two JSON payloads or two URLs.',
    useWhen: 'You have a known-good payload from a spec, a Slack thread, or an older build and want to know exactly what changed.',
    steps: [
      'Paste into Left and Right — JSON or URL; the engine picks the right comparison.',
      'Read the field-level diff: added, removed, changed.',
    ],
    gotchas: [
      'The same engine is available from a captured row\'s detail pane, pre-filled with that request\'s URL or body — often faster than pasting.',
    ],
  },
  {
    path: '/privacy?tab=consent',
    label: 'Privacy → Consent',
    group: 'tools',
    needsTraffic: false,
    what: 'Two tools: decode any TCF / GPP / us_privacy string, and scan the session for consent sent inconsistently.',
    useWhen: 'A bidder claims it did not get consent, or you are verifying a CMP integration.',
    steps: [
      'Decode — paste a raw consent string and read it in plain terms (purposes, vendors, restrictions). No traffic needed.',
      'Session scan — scans captured requests and flags where the same signal was sent differently to different bidders.',
    ],
    gotchas: [
      'The per-bidder inconsistency is usually the real bug; a single decode rarely is.',
    ],
  },
  {
    path: '/inspect?tab=vast',
    label: 'Inspect → VAST',
    group: 'tools',
    needsTraffic: false,
    what: 'Paste any VAST/VMAP XML and get a structured breakdown.',
    useWhen: 'Video is not playing, or an impression is not counting, and you need to know whether the creative or the request is at fault.',
    steps: [
      'Paste the XML.',
      'Read the ad system / id / title, the full wrapper chain, the impression and tracking-event inventory, and the media files.',
      'Check the macro audit — unsubstituted [MACRO] / %%MACRO%% tokens left in the XML.',
    ],
    gotchas: [],
  },
  {
    path: '/captures',
    label: 'Captures',
    group: 'tools',
    needsTraffic: false,
    what: 'Your saved HAR captures — open one on the Dashboard, find every ad unit in it, download, favourite or delete.',
    useWhen: 'You want to revisit a past review without reconnecting a phone, or check which IUs a capture used.',
    steps: [
      'Open — loads the capture into the Dashboard as if it were live.',
      'Find IUs — opens Checks → IU Finder with this capture picked and lists every ad unit in it.',
      'Star a capture to keep it past the 90-day retention.',
    ],
    gotchas: ['The list shows your own saved HARs; IU Finder\'s time windows search everyone\'s.'],
  },
  {
    path: '/checks?mode=iu',
    label: 'Checks → IU Finder',
    group: 'tools',
    needsTraffic: false,
    what: 'Which ad unit does a page use? Searches saved HARs and the live capture.',
    useWhen: 'Someone asks "what IU do World Cup pages send?" or "which pages serve this IU?" and you do not want to connect a phone.',
    steps: [
      'Page → IU: type a topic, section or tag. IU → Pages: paste an ad-unit path.',
      'Pick the saved-HAR window and whether to include the live capture, then Search.',
      'Read one card per page template: IUs with HAR and live counts side by side. A Drift chip means one page type uses more than one ad-unit path on a platform.',
    ],
    gotchas: [
      'No match is not proof the page has no IU — widen the window or capture the page.',
      'USB captures are request-only and some URLs are cut at ~4KB, so those rows can lack an IU.',
      'HAR and live counts are not summed: a live row saved into a HAR would count twice.',
    ],
  },
  {
    path: '/inspect',
    label: 'Inspect → GAM request',
    group: 'tools',
    needsTraffic: false,
    what: 'Paste a GAM/GPT ad-request URL and get every parameter explained.',
    useWhen: 'Someone sends you a GAM URL and asks "does this look right?"',
    steps: [
      'Paste the URL.',
      'Read the per-parameter explanation, backed by a ~40-parameter dictionary.',
      'Check the missing-signal flags — what the request should have carried but did not. Usually more useful than what it did carry.',
    ],
    gotchas: [],
  },
  {
    path: '/inspect?tab=ortb',
    label: 'Inspect → OpenRTB',
    group: 'tools',
    needsTraffic: false,
    what: 'A structural OpenRTB 2.5/2.6 linter. Paste a bid request or bid response, get findings grouped by severity.',
    useWhen: 'Validating a payload against the spec — wrong types, missing required objects, 2.6 fields in a 2.5 request.',
    steps: [
      'Paste the JSON.',
      'Read the findings, grouped by severity.',
    ],
    gotchas: [],
  },
  {
    path: '/privacy?tab=ids',
    label: 'Privacy → IDs & supply chain',
    group: 'tools',
    needsTraffic: true,
    what: 'Identity and supply-chain audit of the live session.',
    useWhen: 'Doing a privacy pass, or chasing "why is this buyer seeing an IDFA it should not?"',
    steps: [
      'Capture a session.',
      'IDs — which device/user identifiers appear where, flagged for leaks (an ID sent to a party that should not get it), inconsistencies (different values for the same ID across requests), and zeroed IDs.',
      'Schain — the parsed supply path as declared.',
    ],
    gotchas: [],
  },
  {
    path: '/traffic?tab=chaos',
    label: 'Traffic control → Chaos',
    group: 'tools',
    needsTraffic: false,
    what: 'Fault injection — make the network misbehave on purpose.',
    useWhen: 'Testing timeout handling, no-fill paths, and SDK failure behaviour.',
    steps: [
      'Add a rule: match on request type, host, or a URL substring.',
      'Pick an action — Delay (adds latency), Block (drops the request), or Mock response (your own status, content type, and body).',
      'Enable the rule and re-trigger the app.',
    ],
    gotchas: [
      'Block and mock short-circuit the request before it is broadcast, so those rows do not appear in the request table — they are console-logged only. Delayed rows do appear, tagged "chaos".',
      'Rules persist on the proxy and affect every device pointed at it. Disable them when you are done.',
    ],
  },
  {
    path: '/privacy?tab=appads',
    label: 'Privacy → app-ads.txt',
    group: 'tools',
    needsTraffic: false,
    what: 'Verifies the app\'s public app-ads.txt against the sellers actually observed in traffic. Needs no seller-account access.',
    useWhen: 'Onboarding a new demand partner, or investigating unauthorized resale.',
    steps: [
      'Enter the developer\'s domain.',
      'The proxy fetches and parses the public app-ads.txt.',
      'Read the reconciliation: which ad systems are authorized, and which are selling your inventory without being declared.',
    ],
    gotchas: [],
  },
  {
    path: '/trends',
    label: 'Trends',
    group: 'tools',
    needsTraffic: false,
    what: 'The cross-session view. Every other page is one session; this is the corpus of all captured HARs, per app and per platform.',
    useWhen: 'You need to know whether what you are seeing is a trend or a one-off.',
    steps: [
      'Pick the app.',
      'Read the per-platform cohort history — CPM, fill, EIDs, and the 14-point vendor-stack row — each with a pass/watch/off chip and a sparkline.',
      'If a live session is connected it is compared against its cohort and drawn as a distinct newest point on top of the historical series.',
      'Watch for the divergence banner: two platforms disagreeing ("CPMs down 32% on iOS while Android is flat") usually means an implementation bug on one platform.',
      'Read the session-level advisories for what is off in the current capture.',
    ],
    gotchas: [
      'The baseline is the trailing 7 days, or the last 5 sessions.',
    ],
  },

  // ── Admin ────────────────────────────────────────────────────────────────
  {
    path: '/admin',
    label: 'Admin Panel',
    group: 'admin',
    needsTraffic: false,
    what: 'Device pairing and session storage.',
    useWhen: 'Setting up a device, or finding an old session.',
    steps: [
      'Devices — allow or block devices by IP; scan the LAN to find them.',
      'QR / profile — pair a device with the QR code, or the generated iOS .mobileconfig (proxy settings plus the CA certificate).',
      'HAR library — saved sessions: open, favourite (favourites are exempt from eviction), or delete.',
    ],
    gotchas: [
      'Non-favourites age out after 60 days, or sooner if total size exceeds the cap.',
      'A device count that will not clear is stale browser state, not the proxy — localStorage.clear() fixes it.',
    ],
  },
  {
    path: '/config',
    label: 'Config',
    group: 'admin',
    needsTraffic: false,
    what: 'UI-editable proxy configuration. Everything here is persisted on the proxy — no file editing.',
    useWhen: 'Adding an app, tuning what the driver exercises, or bypassing a cert-pinned host.',
    steps: [
      'Device config and playlists — apps, flows, and the scan sweeps the driver runs.',
      'SSL exclude — hosts to bypass MITM (cert-pinned hosts that would otherwise break the app).',
      'Monitor config — hygiene-check severities, webhook, and toggles.',
      'App profiles and baselines.',
    ],
    gotchas: [],
  },
  {
    path: '/knowledge',
    label: 'Knowledge',
    group: 'admin',
    needsTraffic: false,
    what: 'The vendor knowledge packs (IAB, Prebid Mobile, GMA, IMA, APS, Xandr, SpringServe…) that ground every validator, check compile, and answer.',
    useWhen: 'You want to see what the tool knows, how fresh it is, or update a pack.',
    steps: [
      'Browse the packs and what each one asserts.',
      'Check each pack\'s freshness indicator.',
      'Run a supervised update when a spec has moved.',
    ],
    gotchas: [
      'These are retrieval-grounded citation packs, not a fine-tuned model. Updating a pack changes what the validators cite immediately.',
    ],
  },
];

// Fast path lookup. Routes here are exact (the app has no nested/param routes
// in NAV_TOOLS), so an exact match is correct — but fall back to a prefix match
// so a future /foo/:id still resolves to /foo's help rather than nothing.
// `search` lets a tabbed page (Checks ?mode=, Releases/Privacy ?tab=) show
// the help for the tab on screen; an entry for the default tab uses the bare path.
export function getHelpForPath(pathname = '/', search = '') {
  const tabbed = search && PAGE_HELP.find((h) => h.path === pathname + search);
  if (tabbed) return tabbed;
  const exact = PAGE_HELP.find((h) => h.path === pathname);
  if (exact) return exact;
  const prefixed = PAGE_HELP
    .filter((h) => h.path !== '/' && pathname.startsWith(h.path))
    .sort((a, b) => b.path.length - a.path.length)[0];
  return prefixed || null;
}

// "Which page do I want?" — the index page's router. Kept alongside the entries
// so a new tool can add its answer in the same commit that adds its help.
export const PAGE_PICKER = [
  ['See what is happening right now', '/'],
  ['Sign off a feature against its acceptance criteria', '/checks?mode=signoff'],
  ['Look up who signed off on what', '/checks?mode=signoff'],
  ['Ask a question about the traffic', '/checks'],
  ['Write a one-off validation in plain English', '/checks'],
  ['Understand a GAM URL someone sent me', '/inspect'],
  ['Check a payload against the OpenRTB spec', '/inspect?tab=ortb'],
  ['Compare this build\'s payload to last build\'s', '/inspect?tab=diff'],
  ['Find out why video will not play', '/inspect?tab=vast'],
  ['Check consent is consistent across bidders', '/privacy?tab=consent'],
  ['Do a privacy / ID-leak pass', '/privacy'],
  ['Find undeclared sellers', '/privacy?tab=appads'],
  ['Test timeout / no-fill behaviour', '/traffic?tab=chaos'],
  ['Point prod traffic at test ad units', '/traffic'],
  ['See if this is a trend or a one-off', '/trends'],
  ['Pair a device / find an old session', '/admin'],
];
